import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

const TOKEN_KEY = "jf_admin_token";

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || null);
  const [admin, setAdmin] = useState(null);
  const [checking, setChecking] = useState(Boolean(token));

  const persistToken = useCallback((t) => {
    if (t) {
      localStorage.setItem(TOKEN_KEY, t);
    } else {
      localStorage.removeItem(TOKEN_KEY);
    }
    setToken(t);
  }, []);

  const logout = useCallback(() => {
    persistToken(null);
    setAdmin(null);
  }, [persistToken]);

  // Attach Authorization header for all axios calls when token is present.
  useEffect(() => {
    const id = api.interceptors.request.use((cfg) => {
      const t = localStorage.getItem(TOKEN_KEY);
      if (t) cfg.headers.Authorization = `Bearer ${t}`;
      return cfg;
    });
    const respId = api.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err?.response?.status === 401) {
          // Token invalid/expired
          persistToken(null);
          setAdmin(null);
        }
        return Promise.reject(err);
      }
    );
    return () => {
      api.interceptors.request.eject(id);
      api.interceptors.response.eject(respId);
    };
  }, [persistToken]);

  // Verify token on mount
  useEffect(() => {
    if (!token) {
      setChecking(false);
      return;
    }
    let cancelled = false;
    api
      .get("/admin/me")
      .then((res) => !cancelled && setAdmin(res.data))
      .catch(() => !cancelled && persistToken(null))
      .finally(() => !cancelled && setChecking(false));
    return () => { cancelled = true; };
  }, [token, persistToken]);

  const login = useCallback(async (username, password) => {
    const res = await api.post("/admin/login", { username, password });
    persistToken(res.data.token);
    setAdmin({ username: res.data.username, role: "admin" });
    return res.data;
  }, [persistToken]);

  return (
    <AuthContext.Provider value={{ token, admin, checking, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
